import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  PropertySalesProps,
  UpdatePropertySalesPricesForm,
  UpdatePropertySalesStateForm,
} from '../models/propertyModel';
import {
  markPropertyAsSoldEndpoint,
  updatePropertySalesAvailabilityEndpoint,
  updatePropertySalesPricesAvailabilityEndpoint,
} from '../services/propertySalesService';
import { useToast } from '../../../components/toast/ToastProvider';
import { ESTIMATE_REFRESH_EVENT } from './usePropertyEstimate';
import usePropertySalesInfo from './usePropertySalesInfo';
import { refreshProperty } from './propertyRefreshEvent';

// Formulario compartido de info de venta (una sola instancia, elevada a la pantalla).
type SalesInfoForm = ReturnType<typeof usePropertySalesInfo>;

const usePropertySalesPrices = (
  props: PropertySalesProps,
  salesInfo: SalesInfoForm,
) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const toast = useToast();
  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    register,
    reset,
  } = useForm<UpdatePropertySalesPricesForm>({
    resolver: yupResolver(
      yup
        .object({
          sale_price: yup
            .number()
            .min(0)
            .required('Precio de venta es requerido')
            .typeError('Precio de venta es requerido en formato numérico'),
          sale_min_price: yup
            .number()
            .max(
              yup.ref('sale_price'),
              'Precio de venta mínimo tiene que ser menor o igual al precio de venta',
            )
            .required('Precio mínimo de venta es requerido'),
        })
        .required(),
    ),
  });

  const updateMarketplaceStatus = async (
    property_id: string,
    form: UpdatePropertySalesStateForm,
  ) => {
    const { status } = await callEndpoint(
      updatePropertySalesAvailabilityEndpoint(form, property_id, token),
    );
    if (status !== 200) {
      toast.error('No se pudo actualizar el estado de la propiedad.');
      return;
    }
    toast.success('Estado de la propiedad actualizado.');
    refreshProperty();
  };

  // Guarda en orden: precios -> fecha de disponibilidad. Los valores financieros tienen su propia
  // tarjeta y su propio botón. Si un paso falla se avisa con un toast y no se sigue.
  const updateMarketplacePrices = async (
    form: UpdatePropertySalesPricesForm,
  ) => {
    // Se valida la fecha antes de tocar nada para no dejar los precios guardados a medias.
    if (!salesInfo.availableAt) {
      toast.error('La fecha de disponibilidad es requerida.');
      return;
    }

    const { status } = await callEndpoint(
      updatePropertySalesPricesAvailabilityEndpoint(
        form,
        props.property_id,
        token,
      ),
    );
    if (status !== 200) {
      toast.error('No se pudieron guardar los precios. Intenta de nuevo.');
      return;
    }
    toast.success('Precios de venta actualizados.');

    // saveAvailability ya avisa con su propio toast si falla.
    if (await salesInfo.saveAvailability()) {
      toast.success('Fecha de disponibilidad actualizada.');
    }
    // Los precios ya cambiaron aunque falle la fecha: se refresca igual.
    // El estimate de venta se recalcula en segundo plano: la tarjeta se refresca sola.
    window.dispatchEvent(new Event(ESTIMATE_REFRESH_EVENT));
    refreshProperty();
  };

  const markPropertyAsSold = async (propertyId: string) => {
    //
    const { status } = await callEndpoint(
      markPropertyAsSoldEndpoint(propertyId, token),
    );
    if (status !== 200) {
      toast.error('No se pudo marcar la propiedad como vendida.');
      return;
    }
    toast.success('Propiedad marcada como vendida.');
    refreshProperty();
  };

  useEffect(() => {
    reset({
      sale_price: props.property_sale_price_value,
      sale_min_price: props.property_sale_min_price_value,
    });
  }, []);

  return {
    control,
    register,
    errors,
    handleSubmit,
    isLoading,
    updateMarketplaceStatus,
    updateMarketplacePrices,
    markPropertyAsSold,
  };
};

export default usePropertySalesPrices;
